import {
  Avatar,
  AvatarSkeleton,
  AvatarWithOverlay,
  Button,
  Icon,
  Skeleton
} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {forwardRef} from 'react';

import type {ComponentPropsWithoutRef} from 'react';

const ACCOUNT_NAV_LAYOUT = cn(
  'flex items-center gap-2 rounded-xl p-3',
  'group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0'
);

export interface AccountNavFirm {
  name: string;
  initials: string;
  logoUrl?: string;
}

export interface AccountNavProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  name: string;
  role?: string;
  /** Shows a skeleton in place of `role` while it's still resolving. */
  isRoleLoading?: boolean;
  initials: string;
  src?: string;
  seed?: string;
  /**
   * When provided, the card leads with the firm: its logo carries the user's avatar as an overlay,
   * the firm name is the title and "name · role" the second line. Left unset (e.g. ops-web), the
   * card shows the user alone.
   */
  firm?: AccountNavFirm;
}

export const AccountNav = forwardRef<HTMLButtonElement, AccountNavProps>(
  ({name, role, isRoleLoading, initials, src, seed, firm, className, ...rest}, ref) => {
    const title = firm?.name ?? name;
    const secondaryLine = firm ? [name, role].filter(Boolean).join(' · ') : role;
    const hasSecondaryLine = isRoleLoading || Boolean(secondaryLine);
    const userAvatar = {initials, alt: name, src, seed};

    return (
      <Button
        ref={ref}
        variant="secondary"
        className={cn(
          'relative h-auto w-full justify-start overflow-visible whitespace-normal font-normal shadow-none',
          ACCOUNT_NAV_LAYOUT,
          'group-data-[collapsible=icon]:bg-transparent',
          'group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:ring-0',
          className
        )}
        {...rest}
      >
        <div className="absolute end-1.25 top-1/2 -translate-y-1/2 flex size-8 items-center justify-center rounded-sm group-data-[collapsible=icon]:hidden">
          <Icon name="chevrons-updown" className="size-5 text-fg-secondary" />
        </div>

        {firm ? (
          <AvatarWithOverlay
            avatar={{
              size: 'lg',
              shape: 'square',
              initials: firm.initials,
              alt: firm.name,
              src: firm.logoUrl,
              seed: firm.name
            }}
            overlay={userAvatar}
          />
        ) : (
          <Avatar size="lg" {...userAvatar} />
        )}

        <div
          className={cn(
            // pe-6 keeps long text clear of the absolutely-positioned chevron above.
            'flex min-w-0 flex-1 flex-col items-stretch pe-6 text-start group-data-[collapsible=icon]:hidden',
            !hasSecondaryLine && 'justify-center'
          )}
        >
          <span className="truncate text-sm font-semibold text-fg">{title}</span>
          {isRoleLoading ? (
            <Skeleton className="mt-0.5 h-3.5 w-16" />
          ) : (
            secondaryLine && (
              <span className="truncate text-sm text-fg-tertiary">{secondaryLine}</span>
            )
          )}
        </div>
      </Button>
    );
  }
);

AccountNav.displayName = 'AccountNav';

/** Placeholder with the same footprint as `AccountNav`, for before the user is known. */
export const AccountNavSkeleton = () => (
  <div aria-hidden data-slot="account-nav-skeleton" className={ACCOUNT_NAV_LAYOUT}>
    <AvatarSkeleton size="lg" />
    <div className="flex flex-1 flex-col gap-1.5 group-data-[collapsible=icon]:hidden">
      <Skeleton className="h-3.5 w-28" />
      <Skeleton className="h-3 w-16" />
    </div>
  </div>
);
